import { lazy, Suspense, useEffect, useState } from 'react'
import { api, ApiError, type Me } from './lib/api'
import { cocok, RouterProvider, useRouter } from './lib/router'
import { ErrorState } from './components/StateBlock'
import { OverlayHost } from './components/overlay'
import { LandingPage } from './pages/LandingPage'
import { SharePage } from './pages/SharePage'

/**
 * Akar aplikasi.
 *
 * Halaman share dimuat dalam bundel utama karena itulah permukaan yang paling
 * sering dibuka orang luar dari ponsel. Sisa aplikasi — yang memerlukan sesi —
 * dipisahkan ke bundel tersendiri dan baru diunduh ketika benar-benar dibuka.
 */
const AppAuthenticated = lazy(() => import('./AppAuthenticated'))

export function App() {
  return (
    <RouterProvider>
      <Rute />
      <OverlayHost />
    </RouterProvider>
  )
}

function Rute() {
  const { path } = useRouter()

  const share = cocok('/s/:token', path)
  if (share?.token) return <SharePage token={share.token} />

  // Akar situs: tamu melihat halaman perkenalan; yang sudah masuk langsung
  // diantar ke aplikasi tanpa perlu menekan apa pun.
  if (path === '/') return <Akar />

  return <GerbangSesi />
}

function Akar() {
  const [status, setStatus] = useState<'memeriksa' | 'tamu'>('memeriksa')

  useEffect(() => {
    const ac = new AbortController()
    api
      .me(ac.signal)
      .then(() => {
        window.location.replace('/app/beranda')
      })
      .catch((err: unknown) => {
        if (err instanceof DOMException && err.name === 'AbortError') return
        setStatus('tamu')
      })
    return () => ac.abort()
  }, [])

  if (status === 'memeriksa') return <Kosong label="Memuat" />
  return <LandingPage />
}

function GerbangSesi() {
  const [user, setUser] = useState<Me | null>(null)
  const [memuat, setMemuat] = useState(true)
  const [galat, setGalat] = useState<string | null>(null)

  useEffect(() => {
    const ac = new AbortController()
    api
      .me(ac.signal)
      .then((me) => {
        setUser(me)
        setGalat(null)
      })
      .catch((err: unknown) => {
        if (err instanceof DOMException && err.name === 'AbortError') return
        if (err instanceof ApiError && err.perluMasuk) {
          const kembali = window.location.pathname + window.location.search
          window.location.href = `/login?redirect=${encodeURIComponent(kembali)}`
          return
        }
        setGalat(err instanceof ApiError ? err.message : 'Tidak dapat menghubungi server.')
      })
      .finally(() => {
        if (!ac.signal.aborted) setMemuat(false)
      })
    return () => ac.abort()
  }, [])

  if (memuat) return <Kosong label="Memuat" />

  if (galat) {
    return (
      <div style={{ minHeight: '100dvh', display: 'grid', placeItems: 'center' }}>
        <ErrorState message={galat} onRetry={() => window.location.reload()} />
      </div>
    )
  }

  if (!user) return null

  return (
    <Suspense fallback={<Kosong label="Memuat aplikasi" />}>
      <AppAuthenticated user={user} />
    </Suspense>
  )
}

/**
 * Layar tunggu tanpa pemintal: pemuatannya biasanya di bawah 200 ms, dan
 * pemintal yang berkelebat justru membuat aplikasi terasa lebih lambat
 * daripada layar yang diam sejenak.
 */
function Kosong({ label }: { label: string }) {
  return (
    <div style={{ minHeight: '100dvh', display: 'grid', placeItems: 'center' }}>
      <span className="sr-only">{label}</span>
    </div>
  )
}
